import {useEffect, useMemo, useState} from 'react';
import './activity-type-entry.scss';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../i18n/translations';
import {Alert, Box} from '@mui/material';
import {ActivityTypeEntrySkeleton} from './skeleton';
import {convertArrayToIndex} from '../../../utils/functions';
import MedicationCardContent from './medication-card-content';
import MedicalAppointmentCardContent from './medical-appointment-card-content';
import LinesOrColumnsCardContent from './lines-or-columns-card-content';
import {CarePlanActivitiesGroupType} from '../../../backend/interfaces/care-plan-activities-group-type';
import {ActivityType} from '../../../backend/interfaces/activity-type';
import {CarePlanConfiguration} from '../../../backend/interfaces/care-plan-configuration';
import {EntityActivitiesRepository} from '../../../repositories/entity-activities';
import {CarePlanConfigurationFeature} from '../../../backend/enums/care-plan-configuration-feature';
import {CarePlanActivityTypesArrangement} from '../../../backend/enums/care-plan-activity-types-arrangement';
import {CarePlanActivitiesGroupApiRequestConfigurationsItem} from '../../../backend/requests/care-plan-activities-group';
import {LoadingConfigurationsType} from '../../../enums';
import {ConfigurationsIndex, ConfigurationValueType} from '../../../types';

interface CarePlanSheetActivityTypeEntryProps {
  activitiesSectorId: string;
  activitiesGroupId: string;
  entry: CarePlanActivitiesGroupType;
  layoutType: CarePlanActivityTypesArrangement;
  onEditMode: boolean;
  onClick: () => void;
  periodsOfTheDay: ActivityType[] | undefined;
}

export const CarePlanSheetActivityTypeEntry = ({
  activitiesSectorId,
  activitiesGroupId,
  entry,
  layoutType,
  onEditMode,
  onClick,
  periodsOfTheDay,
}: CarePlanSheetActivityTypeEntryProps) => {
  const {t} = useTranslation();

  const {entityId}: {entityId: string} = useParams();

  // Hold state
  const [loadingConfigurations, setLoadingConfigurations] = useState<LoadingConfigurationsType>(
    LoadingConfigurationsType.Loading
  );

  const [activityType, setActivityType] = useState<ActivityType | undefined>(undefined);

  const [configurationsIndex, setConfigurationsIndex] = useState<ConfigurationsIndex | undefined>(
    undefined
  );

  const [activationStateConfigurationInfo, setActivationStateConfigurationInfo] =
    useState<ConfigurationValueType<boolean>>(undefined);

  const [nameConfigurationInfo, setNameConfigurationInfo] =
    useState<ConfigurationValueType<string>>(undefined);

  // On mount
  useEffect(() => {
    // Start Loading Configurations
    setLoadingConfigurations(LoadingConfigurationsType.Loading);

    // Obtain data
    Promise.all<[Promise<ActivityType | null>, Promise<Array<CarePlanConfiguration>>]>([
      // Load Activity Type (to obtain the Configuration layout + icon + name + etc)
      EntityActivitiesRepository.instance.getType(
        entityId,
        activitiesSectorId,
        activitiesGroupId,
        entry.activityTypeId
      ),

      // Load Activity Type Configurations (to list the configuration values)
      EntityActivitiesRepository.instance.getTypeCarePlanConfigurations(
        entityId,
        activitiesSectorId,
        activitiesGroupId,
        entry.activityTypeId
      ),
    ])
      .then(
        ([activityType, careplanConfigurations]: [
          ActivityType | null,
          Array<CarePlanConfiguration>,
        ]) => {
          if (activityType) {
            // Build Configurations Index
            const configurationsIndex = convertArrayToIndex<CarePlanConfiguration>(
              careplanConfigurations,
              'id'
            );

            // Get Id of "activation-state" Feature
            let activationStateFeatureId: string | undefined = undefined;

            for (const careplanConfiguration of careplanConfigurations) {
              if (careplanConfiguration.feature === CarePlanConfigurationFeature.ActivationState) {
                // Set id
                activationStateFeatureId = careplanConfiguration.id;

                // Stop search
                break;
              }
            }

            // Get Id of "name" Feature
            let nameFeatureId: string | undefined = undefined;

            for (const careplanConfiguration of careplanConfigurations) {
              if (careplanConfiguration.feature === CarePlanConfigurationFeature.Name) {
                // Set id
                nameFeatureId = careplanConfiguration.id;

                // Stop search
                break;
              }
            }

            // Evaluate Activation State (if exists)
            if (activationStateFeatureId) {
              // Flag
              let valueFound = false;

              // If entry has configurations
              if (entry.configurations) {
                // Get value
                for (const configuration of entry.configurations) {
                  if (configuration.carePlanConfigurationId === activationStateFeatureId) {
                    // Set value
                    setActivationStateConfigurationInfo({
                      configuration: configurationsIndex[activationStateFeatureId],
                      value: configuration.carePlanConfigurationValue,
                    });

                    // Set flag
                    valueFound = true;

                    // Stop search
                    break;
                  }
                }
              }

              // If value not found
              if (!valueFound) {
                // Get default value from configuration layout
                const configurationLayout =
                  activityType.carePlanConfigurationLayout?.configurations?.find(
                    (configuration) => {
                      return configuration.carePlanConfigurationId === activationStateFeatureId;
                    }
                  );

                // If found
                if (configurationLayout && configurationLayout.defaultValue) {
                  // Set value
                  setActivationStateConfigurationInfo({
                    configuration: configurationsIndex[activationStateFeatureId],
                    value: configurationLayout.defaultValue,
                  });
                }
              }
            }

            // Evaluate Name (if exists)
            if (nameFeatureId) {
              // If entry has configurations
              if (entry.configurations) {
                // Get value
                for (const configuration of entry.configurations) {
                  if (configuration.carePlanConfigurationId === nameFeatureId) {
                    // Set value
                    setNameConfigurationInfo({
                      configuration: configurationsIndex[nameFeatureId],
                      value: configuration.carePlanConfigurationValue,
                    });

                    // Stop search
                    break;
                  }
                }
              }
            }

            // Set
            setActivityType(activityType);

            // Set index to state
            setConfigurationsIndex(configurationsIndex);

            // Stop Loading
            setLoadingConfigurations(LoadingConfigurationsType.Loaded);
          } else {
            // Not found
            setLoadingConfigurations(LoadingConfigurationsType.Error);
          }
        }
      )
      .catch((error: any) => {
        // Log
        console.error(error);

        // Stop Loading
        setLoadingConfigurations(LoadingConfigurationsType.Error);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry]);

  const valuesIndex = useMemo(
    () =>
      convertArrayToIndex<CarePlanActivitiesGroupApiRequestConfigurationsItem>(
        entry?.configurations ?? [],
        'carePlanConfigurationId'
      ),
    [entry?.configurations]
  );

  // Build
  return (
    <Box
      className={`
            activity-type-entry-card
            activity-type-entry-card-${layoutType}
            ${onEditMode ? 'activity-type-entry-card-edit' : 'activity-type-entry-card-no-edit'}
                ${
                  activationStateConfigurationInfo?.value === true
                    ? 'activity-type-entry-card-active'
                    : 'activity-type-entry-card-inactive'
                }
                `}
      onClick={onClick}
    >
      {loadingConfigurations === LoadingConfigurationsType.Loading && (
        <ActivityTypeEntrySkeleton layoutType={layoutType} />
      )}

      {loadingConfigurations === LoadingConfigurationsType.Error && (
        <Alert severity="error">
          {t(translations.activities_type_entry.snackbar.configuration.error)}
        </Alert>
      )}

      {loadingConfigurations === LoadingConfigurationsType.Loaded && activityType && (
        <Box className="activity-type-entry-card-content">
          <Box
            className={`activity-type-statebar activity-type-statebar-${
              activationStateConfigurationInfo?.value === true ? 'active' : 'inactive'
            }`}
            sx={{
              height: '10px',
              width: '10px',
            }}
          />
          <div className="activity-type-info">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                overflow:
                  layoutType === CarePlanActivityTypesArrangement.Columns
                    ? 'hidden'
                    : 'auto hidden',
                margin:
                  layoutType === CarePlanActivityTypesArrangement.Columns ? 'unset' : '0 10px 0 0',
                width: '100%',
              }}
            >
              {layoutType === CarePlanActivityTypesArrangement.Medication && (
                <MedicationCardContent
                  entityId={entityId}
                  activitiesSectorId={activitiesSectorId}
                  activitiesGroupId={activitiesGroupId}
                  configurationsIndex={configurationsIndex}
                  valuesIndex={valuesIndex}
                  periodsOfTheDay={periodsOfTheDay}
                  activityType={activityType}
                />
              )}

              {layoutType === CarePlanActivityTypesArrangement.MedicalAppointment && (
                <MedicalAppointmentCardContent
                  configurationsIndex={configurationsIndex}
                  valuesIndex={valuesIndex}
                  activityType={activityType}
                />
              )}

              {(layoutType === CarePlanActivityTypesArrangement.Lines ||
                layoutType === CarePlanActivityTypesArrangement.Columns) && (
                <LinesOrColumnsCardContent
                  layoutType={layoutType}
                  configurationsIndex={configurationsIndex}
                  valuesIndex={valuesIndex}
                  activityType={activityType}
                  nameConfigurationInfo={nameConfigurationInfo}
                  activationStateConfigurationInfo={activationStateConfigurationInfo}
                />
              )}
            </Box>
          </div>
        </Box>
      )}
    </Box>
  );
};
